import { FC, useMemo, useState } from 'react';
import LaunchIcon from '@mui/icons-material/Launch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import { MutualFundMark } from 'services/hooks/companies';
import {
  CardBody,
  CardTitle,
  CardTitleRow,
  OutlinedCard,
} from '../CompanyOverview.styles';
import {
  AvgPrice,
  ChangeLabel,
  FundFilterRow,
  FundPill,
  HeaderLeft,
  MarksTable,
  MarksTableCell,
  MarksTableHead,
  MarksTableRow,
  ViewToggle,
} from './MutualFundMarksCard.styles';
import {
  buildChartSeries,
  computeHeaderStats,
  EndDateMode,
  EndDateModeValue,
  formatCurrency,
  formatXAxisLabel,
  getFundLabel,
  getPeriodStartDate,
  getPricePerShare,
  Period,
  PERIODS,
  PeriodValue,
  toNum,
  ViewMode,
  ViewModeValue,
} from './MutualFundMarksCard.utils';

interface Props {
  companyId: number | undefined;
  data: { funds: string[]; marks: MutualFundMark[] } | undefined;
  isLoading: boolean;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
}

const MutualFundMarksCard: FC<Props> = ({ companyId: _companyId, data, isLoading, measurementDate, endDateMode }) => {
  const [view, setView] = useState<ViewModeValue>(ViewMode.Chart);
  const [selectedFund, setSelectedFund] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodValue>(Period.All);

  const allFunds = data?.funds ?? [];
  const uniqueFundLabels = [...new Set(allFunds.map(getFundLabel))];

  const latestDataDate = useMemo(() => {
    if (!data?.marks.length) {
      return undefined;
    }
    return [...data.marks].sort((a, b) => b.reporting_date.localeCompare(a.reporting_date))[0].reporting_date;
  }, [data]);

  const effectiveEndDate = useMemo(() => endDateMode === EndDateMode.Measurement && measurementDate
    ? measurementDate
    : latestDataDate, [endDateMode, measurementDate, latestDataDate]);

  const filteredMarks = useMemo(() => {
    if (!data) {
      return [];
    }
    let { marks } = data;

    if (effectiveEndDate) {
      marks = marks.filter(m => m.reporting_date <= effectiveEndDate);
    }

    if (period !== Period.All && effectiveEndDate) {
      const startDate = getPeriodStartDate(effectiveEndDate, period);
      if (startDate) {
        marks = marks.filter(m => m.reporting_date >= startDate);
      }
    }

    if (selectedFund) {
      marks = marks.filter(m => getFundLabel(m.fund_name) === selectedFund);
    }

    return marks;
  }, [data, period, effectiveEndDate, selectedFund]);

  const { series, singleFund } = useMemo(() => buildChartSeries(filteredMarks), [filteredMarks]);
  const { headerValue, change } = useMemo(() => computeHeaderStats(filteredMarks), [filteredMarks]);

  const hasData = (data?.marks.length ?? 0) > 0;

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <HeaderLeft>
            <CardTitle component="span">Mutual Fund Marks</CardTitle>
            {hasData && (
              <>
                <AvgPrice>{formatCurrency(headerValue)}</AvgPrice>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  avg price / share
                </Typography>
                {change && (
                  <ChangeLabel positive={change.abs >= 0}>
                    {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                  </ChangeLabel>
                )}
              </>
            )}
          </HeaderLeft>
          <ViewToggle>
            {hasData && (
              <ToggleButtonGroup
                value={period}
                exclusive
                size="small"
                onChange={(_e, v: PeriodValue | null) => {
                  if (v) {
                    setPeriod(v);
                  }
                }}
              >
                {PERIODS.map(p => (
                  <ToggleButton key={p} value={p}>{p}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            <ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={(_e, v: ViewModeValue | null) => {
                if (v) {
                  setView(v);
                }
              }}
            >
              <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
              <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
            </ToggleButtonGroup>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
              href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&type=NPORT-P&dateb=&owner=include&count=40"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', borderColor: 'warning.main', color: 'warning.dark', height: 26, py: 0 }}
            >
              SEC Filing
            </Button>
          </ViewToggle>
        </CardTitleRow>

        {isLoading && (
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
        )}

        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No mutual fund marks available for this company.
          </Typography>
        )}

        {!isLoading && hasData && (
          <>
            {uniqueFundLabels.length > 1 && (
              <FundFilterRow>
                {uniqueFundLabels.map(label => (
                  <FundPill
                    key={label}
                    label={label}
                    size="small"
                    variant="outlined"
                    active={selectedFund === label}
                    onClick={() => setSelectedFund(prev => prev === label ? null : label)}
                  />
                ))}
                <FundPill
                  label="All Funds"
                  size="small"
                  variant="outlined"
                  active={selectedFund === null}
                  onClick={() => setSelectedFund(null)}
                />
              </FundFilterRow>
            )}

            {view === ViewMode.Chart && (
              <ScalarLineChart
                series={series}
                notMerge
                smooth
                showPoints
                style={{ height: 280 }}
                config={{
                  legend: { show: true, position: 'top' },
                  xAxis: { formatter: v => formatXAxisLabel(v, singleFund) },
                  yAxis: { formatter: v => `$${v}` },
                }}
              />
            )}

            {view === ViewMode.Table && (
              <MarksTable>
                <thead>
                  <MarksTableRow>
                    <MarksTableHead>Fund</MarksTableHead>
                    <MarksTableHead>Date</MarksTableHead>
                    <MarksTableHead align="right">Price / Share</MarksTableHead>
                    <MarksTableHead align="right">Fair Value</MarksTableHead>
                    <MarksTableHead align="right">Shares Held</MarksTableHead>
                    <MarksTableHead align="center">Filing</MarksTableHead>
                  </MarksTableRow>
                </thead>
                <tbody>
                  {[...filteredMarks].reverse().map((m, i) => (
                    <MarksTableRow key={i}>
                      <MarksTableCell>{getFundLabel(m.fund_name)}</MarksTableCell>
                      <MarksTableCell>{m.reporting_date}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(getPricePerShare(m))}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.fair_value_usd))}</MarksTableCell>
                      <MarksTableCell align="right">{m.shares_held?.toLocaleString() ?? '—'}</MarksTableCell>
                      <MarksTableCell align="center">
                        <IconButton
                          size="small"
                          component="a"
                          href={m.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main' }}
                        >
                          <LaunchIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </MarksTableCell>
                    </MarksTableRow>
                  ))}
                </tbody>
              </MarksTable>
            )}
          </>
        )}
      </CardBody>
    </OutlinedCard>
  );
};

export { MutualFundMarksCard };
